<template>
  <ion-page>
    <Header />

    <ion-content v-if="!showSearch" :fullscreen="true">
      <div id="top"></div>

      <ion-refresher slot="fixed" @ionRefresh="refresh($event)">
        <ion-refresher-content />
      </ion-refresher>

      <div v-if="userCategories?.length" class="category-filter">
        <div class="category-row">
          <CategorySelector
            :mainLabel="$t('home.all')"
            :active="!selectedDefaultCategory"
            @main-click="onHandleAllClick"
          />
          <CategorySelector
            v-for="category in defaultCategories"
            :key="category.id"
            :mainLabel="category.name.slice(0, 20)"
            :subcategory="selectedSubcategory?.name.slice(0, 20)"
            :active="selectedDefaultCategory?.id === category.id"
            @main-click="onHandleDefaultCategoryClick(category)"
          />
        </div>
        <div v-if="specializationCategories.length > 0" class="category-row">
          <CategorySelector
            v-for="category in specializationCategories"
            :key="category.id"
            :mainLabel="category.name.slice(0, 20)"
            :active="selectedSubcategory?.id === category.id"
            @main-click="onHandleSubcategoryClick(category)"
          />
        </div>
      </div>

      <div id="container" class="m-3">
        <!-- Loading spinner for filter changes -->
        <div v-if="isFilterLoading" class="is-flex ion-align-items-center ion-justify-content-center" style="min-height: 300px;">
          <IonSpinner />
        </div>

        <!-- Filtered view when category is selected -->
        <section v-if="selectedDefaultCategory && !isLoading && !isFilterLoading">
          <FeaturedSlidersContainer :featured-sliders-data="featuredCourses" />

          <PopularCoursesCarousel />

          <CoursesReleasesGrid />
        </section>

        <!-- Default home view -->
        <section v-if="!selectedDefaultCategory && !isLoading && !isFilterLoading" class="home-order-wrapper">
          <app-lazy :style="getStyleOrderPosition('recently_played')">
            <RecentlyPlayedSlider :selected-category="selectedCategoryId" />
          </app-lazy>

          <app-lazy :style="getStyleOrderPosition('popular_courses')">
            <PopularCoursesCarousel :selected-category="selectedCategoryId" />
          </app-lazy>

          <app-lazy :style="getStyleOrderPosition('popular_streams')">
            <Top10PodcastsCarousel
              :title="$t('home.topStreamTitle')"
              :popular-data="popularWeekly"
            />
          </app-lazy>

          <FeaturedSlidersContainer :featured-sliders-data="featuredCourses" />

          <app-lazy :style="getStyleOrderPosition('new_releases')">
            <NewReleasesCarousel :selected-category="selectedCategoryId" />
          </app-lazy>

          <app-lazy :style="getStyleOrderPosition('following')">
            <HomeFollowing :selected-category="selectedCategoryId" />
          </app-lazy>
        </section>

        <div class="footer ion-text-center">
          <div class="podcast-studio">
            <IonImg src="./assets/podcast_studio.png" style="width: 100%" />
            <div class="podcast-studio-text fw-700">
              {{ $t('home.ownPodcast') }}
            </div>
          </div>
          <ion-button
            mode="ios"
            shape="round"
            class="contact-btn fw-500 fz-18"
            :href="JIRA_CONTACT_LINK"
            target="_blank"
          >
            {{ $t('commonKeys.contact') }}
          </ion-button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { watchEffect, watch } from '@vue/runtime-core';
import { computed, defineComponent, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { JIRA_CONTACT_LINK } from '@/config/constants.js';
import Header from '../components/common/Header';
import CategorySelector from '../components/home/CategorySelector';

import {
  IonImg,
  IonPage,
  IonList,
  IonItem,
  IonTitle,
  IonButton,
  IonButtons,
  IonContent,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  onIonViewDidEnter,
  IonTabButton,
  IonTabBar,
  IonLabel,
} from '@ionic/vue';

import { personCircle, settingsSharp, search } from 'ionicons/icons';

import AppImg from '@/components/common/AppImg.vue';
import AudioPlayer from '@/components/player/AudioPlayer';
import NewPodcast from '@/components/common/NewPodcast.vue';
import HomeFollowing from '@/components/home/HomeFollowing';
import GreetingHeader from '@/components/home/GreetingHeader';
import NewReleasesCarousel from '@/components/home/NewReleasesCarousel';
import CoursesReleasesGrid from '@/components/home/CoursesReleasesGrid';
import PopularStreamsCarousel from '@/components/home/PopularStreamsCarousel';
import PopularCoursesCarousel from '@/components/home/PopularCoursesCarousel';
import RecentlyPlayedSlider from '@/components/recently-played/RecentlyPlayedSlider.vue';
import FeaturedSlidersContainer from '@/components/featured/FeaturedSlidersContainer.vue';
import Top10PodcastsCarousel from '@/components/home/Top10PodcastsCarousel.vue';

import {
  INIT_APP_NETWORK_CONNECTED,
  TRACKING_HOME,
  DEEP_LINK_REDIRECT_STORAGE_KEY,
  NOT_FOUNT_REDIRECT_STORAGE_KEY,
} from '@/config/constants';
import { waitTime } from '@/helpers/functions';
import useTracking from '@/composables/useTracking';
import take from 'lodash/take';
import AppLazy from '@/components/common/AppLazy';
import useAppStorage from '@/composables/useAppStorage';
import { useRouter } from 'vue-router';
import axios from '@/http';
import { PROFILE_CATEGORIES } from '@/config/api';

const CATEGORY_ALL = 'all';

export default defineComponent({
  name: 'Home',
  components: {
    AppLazy,
    IonPage,
    IonList,
    IonItem,
    IonImg,
    IonTitle,
    IonButton,
    IonContent,
    IonButtons,
    IonSpinner,
    IonRefresher,
    IonRefresherContent,
    IonTabBar,
    IonTabButton,
    IonLabel,

    AppImg,
    NewPodcast,
    AudioPlayer,
    HomeFollowing,
    GreetingHeader,
    NewReleasesCarousel,
    CoursesReleasesGrid,
    RecentlyPlayedSlider,
    PopularStreamsCarousel,
    PopularCoursesCarousel,
    FeaturedSlidersContainer,
    Top10PodcastsCarousel,
    Header,
    CategorySelector,
  },

  setup() {
    const store = useStore();
    const { trackThis } = useTracking();
    const router = useRouter();

    const orderConfig = computed(() => store.state.app.homeOrderConfig);

    trackThis(TRACKING_HOME);

    const slideOptions = { slidesPerView: 'auto' };

    const isLoading = ref(true);
    const isFilterLoading = ref(false);

    const selectedDefaultCategory = ref(null);
    const selectedSubcategory = ref(null);

    const profileDetailsFetched = computed(() => store.getters['profile/profileDetailsFetched']);

    const userCategories = ref([]);
    
    const userProfessions = computed(() => store.getters['profile/getUserProfessions']);

    const defaultCategories = computed(() => {
      return userCategories.value.filter((category) => {
        const isOnCategoriesProfessions = (profession_id) => {
          return category.professions.some((profession) => profession.id === profession_id);
        };
        
        const isOnProfessions = userProfessions.value.some((profession) => isOnCategoriesProfessions(profession.profession_id));
        const isDefaultCategory = category.profession_default === 1;
        const hasCourses = category.courses_count + category.featured_courses_count > 0;

        return isDefaultCategory && hasCourses && isOnProfessions;
      });
    });

    const specializationCategories = computed(() => {
      if (!selectedDefaultCategory.value) {
        return [];
      }

      const defaultCategoryProfessionId = selectedDefaultCategory.value.professions[0].id;
      
      return userCategories.value.filter((category) => {
        const isDefaultCategory = category.profession_default === 1;
        const hasProfessions = category.professions?.length;
        const includeDefaultCategoryProfession = category.professions?.some((profession) => profession.id === defaultCategoryProfessionId);
        const hasCourses = category.courses_count + category.featured_courses_count > 0;

        return !isDefaultCategory && hasProfessions && includeDefaultCategoryProfession && hasCourses;
      });
    });

    const selectedCategoryId = computed(() => {
      return selectedDefaultCategory.value?.id || selectedSubcategory.value?.id || CATEGORY_ALL;
    });

    const featuredCourses = computed(() => {
      let featuredCourses = store.state.featuredCourses.featuredCoursesDetails;
      
      return featuredCourses;
    });

    const popularWeekly = computed(() => {
      return take(store.getters['library/getPopularStreamsWeekly'], 10);
    });

    const getNewReleases = () =>
      store.dispatch('newReleases/setNewReleasesHome');
    const getRecentlyPlayed = () =>
      store.dispatch('recentlyPlayed/setRecentlyPlayedDetails');
    const getFollows = () => store.dispatch('favorites/setFollowingList');
    const getRecentlyPlayedStorage = () =>
      store.dispatch('player/setRecentlyPlayedStorage');
    const getPopularWeeklyStreams = () =>
      store.dispatch('library/setPopularStreamsWeekly');
    const getPopularCourses = () => store.dispatch('library/setPopularCourses');
    const showSearch = computed(() => store.getters['search/getShowSearch']);

    const getFeaturedCourses = async () => {
      let professions = [];
      try {
        professions = (await store.dispatch('profile/setUserProfessions')).data;
        // eslint-disable-next-line no-empty
      } catch (e) {}
      const params = {};

      if (selectedCategoryId.value !== CATEGORY_ALL) {
        const filterByCategory = selectedSubcategory.value?.id || selectedDefaultCategory.value?.id;
        params.category_ids = [filterByCategory];
      }
      await store.dispatch('featuredCourses/setFeaturedDetails', params);
    };

    onIonViewDidEnter(async () => {
      if (isLoading.value) {
        setTimeout(() => {
          isLoading.value = false;
        }, 0);
      }
      
      await initHome();
    });

    async function refetchPopularAndNewReleases(popularCoursesParams, newReleasesParams) {
      isFilterLoading.value = true;
      try {
        await Promise.all([
          store.dispatch('library/setPopularCourses', popularCoursesParams),
          store.dispatch('newReleases/setNewReleasesHome', newReleasesParams),
        ]);
      } finally {
        isFilterLoading.value = false;
      }
    }

    async function onHandleDefaultCategoryClick(category) {
      if (category.id !== selectedDefaultCategory.value?.id) {
        selectedDefaultCategory.value = category;
      }
      selectedSubcategory.value = null;

      await refetchPopularAndNewReleases({
        customParams: {
          category_ids: [category.id],
        },
      }, {
        customParams: {
          category_ids: [category.id],
        },
      });
    };

    async function onHandleSubcategoryClick(subcategory) {
      if (subcategory.id !== selectedSubcategory.value?.id) {
        selectedSubcategory.value = subcategory;
      }

      await refetchPopularAndNewReleases({
        customParams: {
          category_ids: [subcategory.id],
        },
      }, {
        customParams: {
          category_ids: [subcategory.id],
        },
      });
    };

    async function onHandleAllClick() {
      selectedDefaultCategory.value = null;
      selectedSubcategory.value = null;

      await refetchPopularAndNewReleases();
    };

    const searchbarRef = ref(null);

    const getUserCategories = async () => {
      if (userProfessions.value?.length > 0) {
        try {
          const categories = await axios.get(PROFILE_CATEGORIES);
          userCategories.value = categories.data.categories.filter(c => c.is_active) || [];
        } catch (error) {
          console.error('Error fetching categories:', error);
          userCategories.value = [];
        }
      }
    };

    const fetchAllData = () => {
      getNewReleases();
      getFeaturedCourses();
      getRecentlyPlayed();
      getFollows();
      getRecentlyPlayedStorage();
      getPopularWeeklyStreams();
      getPopularCourses();
      getUserCategories();
    };

    async function redirectCheck() {
      try {
        const { getKey, removeKey } = useAppStorage();

        const keys = [
          DEEP_LINK_REDIRECT_STORAGE_KEY,
          NOT_FOUNT_REDIRECT_STORAGE_KEY,
        ];

        for (const key of keys) {
          const redirect = await getKey(key);

          if (redirect) {
            router.push(redirect);
            await Promise.all([
              removeKey(DEEP_LINK_REDIRECT_STORAGE_KEY),
              removeKey(NOT_FOUNT_REDIRECT_STORAGE_KEY),
            ]);
            return;
          }
        }
      } catch (error) {
        console.error(error);
      }
    }

    async function initHome() {
      if (!profileDetailsFetched.value) {
        await store.dispatch('profile/setProfileDetails');
      }
      await redirectCheck();
      fetchAllData();
    }

    onMounted(async () => {
      await initHome();
    });

    watch(
      () => store.state.favorites.followersUpdated,
      async (newValue) => {
        if (newValue) {
          // for preventing double fetching used delay
          await waitTime(250);
          if (store.state.favorites.followersUpdated) {
            getFollows();
            store.commit('favorites/setFollowingUpdated', false);
          }
        }
      },
    );

    const refresh = async (ev) => {
      fetchAllData();
      ev.target.complete();
    };

    watchEffect(() => {
      const newValue = store.state.app.network.appInitState;
      if (newValue === INIT_APP_NETWORK_CONNECTED) {
        fetchAllData();
      }
    });

    watch(
      () => [selectedDefaultCategory.value, selectedSubcategory.value],
      () => {
        getFeaturedCourses();
      },
    );

    watch(
      () => store.state.app.appActive,
      (appActive) => {
        const platform = store.state.app.platform;
        if (appActive && platform !== 'web') {
          fetchAllData();
        }
      },
    );

    const getStyleOrderPosition = (el) => {
      const order = orderConfig.value[el] ?? 0;
      return { order };
    };

    return {
      search,
      personCircle,
      settingsSharp,
      isLoading,
      slideOptions,
      popularWeekly,
      featuredCourses,
      userCategories,
      userProfessions,
      selectedDefaultCategory,
      selectedSubcategory,
      selectedCategoryId,
      defaultCategories,
      specializationCategories,
      JIRA_CONTACT_LINK,
      searchbarRef,
      showSearch,
      isFilterLoading,
      onHandleDefaultCategoryClick,
      refresh,
      getStyleOrderPosition,
      onHandleAllClick,
      onHandleSubcategoryClick,
    };
  },
});
</script>

<style scoped lang="scss">
.new-wrap {
  flex-wrap: wrap;
}

.title {
  color: var(--ion-color-primary);
}
.footer {
  margin: 30px 0px;

  .podcast-studio {
    position: relative;
    border-radius: 10px;
    overflow: hidden;
  }
  .podcast-studio-text {
    width: 100%;
    position: absolute;
    bottom: 0;
    text-align: center;
    font-size: 40px;
  }
  .contact-btn {
    --padding-start: 40px;
    --padding-end: 40px;
    margin-top: 20px;
  }
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
}
.search-bar {
  width: 600px;
}
ion-searchbar {
  --border-radius: 8px;
}

:deep(.searchbar-search-icon) {
  display: none !important;
}
:deep(.searchbar-input) {
  padding-left: 10px !important;
}

.category-filter {
  z-index: 1;
  padding: 10px;
}

.category-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.category-row:last-child {
  margin-bottom: 0;
}

ion-button {
  font-size: 14px;
  --padding-start: 12px;
  --padding-end: 12px;
  border-radius: 20px;
  color: var(--ion-color-light);
  background-color: var(--grey);
}
</style>
